import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold leading-none tracking-[0.01em] [&>svg]:size-3 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30 aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[background-color,color,border-color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-primary/20 bg-accent text-primary [a&]:hover:border-primary [a&]:hover:bg-accent",
        secondary:
          "border-border bg-muted text-muted-foreground [a&]:hover:border-foreground/15 [a&]:hover:text-foreground",
        destructive:
          "border-[var(--go-berry)]/25 bg-[color:rgba(157,11,77,0.06)] text-[var(--go-berry)] [a&]:hover:border-[var(--go-berry)] [a&]:hover:bg-[color:rgba(157,11,77,0.12)] focus-visible:ring-destructive/20",
        outline:
          "border-border bg-background text-foreground [a&]:hover:border-primary [a&]:hover:bg-accent",
        vulnerability:
          "border-[var(--go-purple)]/30 bg-[var(--go-vulnerability-bg)] text-[var(--go-purple)]",
        upheld:
          "border-[var(--go-berry)]/30 bg-[color:rgba(157,11,77,0.06)] text-[var(--go-berry)]",
        notUpheld:
          "border-[var(--go-teal)]/30 bg-[color:rgba(0,162,198,0.06)] text-[var(--go-teal)]",
        partiallyUpheld:
          "border-[var(--go-purple)]/30 bg-[color:rgba(88,0,149,0.06)] text-[var(--go-purple)]",
        success:
          "border-[var(--go-success)]/30 bg-[color:rgba(22,163,74,0.06)] text-[var(--go-success)]",
        attention:
          "border-[var(--go-bittersweet)]/30 bg-[color:rgba(255,116,97,0.08)] text-[var(--go-bittersweet)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
